import './DateRangeSection.sass';
import '../../../uikit/DatePicker/DatePickerVender.css';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import formatDate from 'date-fns/format';
import subMonths from 'date-fns/subMonths';
import { fromUnixTime } from 'date-fns';

import { List, ListItem } from '$uikit';
import { IcCheck16 } from '$assets';
import { useTranslator } from '$hooks';

export type DateRangePreset = '7d' | '30d' | '90d' | '180d' | 'custom';

export const PRESET_KEYS: Record<DateRangePreset, string> = {
  '7d': 'statistics_pro_period_7d',
  '30d': 'statistics_pro_period_30d',
  '90d': 'statistics_pro_period_90d',
  '180d': 'statistics_pro_period_180d',
  custom: 'statistics_pro_period_custom',
};

export const PRESET_DEFAULTS: Record<DateRangePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '180d': 'Last 180 days',
  custom: 'Custom range',
};

export interface DateRangeSectionProps {
  preset: DateRangePreset;
  startDay: number;
  endDay: number;
  onChange: (preset: DateRangePreset, startDay: number, endDay: number) => void;
  onPick?: (preset: DateRangePreset) => void;
}

const PRESET_VALUES: DateRangePreset[] = ['7d', '30d', '90d', '180d', 'custom'];

const toUnixDay = (d: Date) =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000);

const formatDay = (unix: number) => formatDate(fromUnixTime(unix), 'd MMM');

const tomorrowMidnight = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
})();
const EXCLUDE_FUTURE = [{ start: tomorrowMidnight, end: new Date('3000') }];

const MIN_DATE = (() => {
  const d = subMonths(new Date(), 6);
  d.setHours(0, 0, 0, 0);
  return d;
})();

export const DateRangeSection: FC<DateRangeSectionProps> = ({
  preset,
  startDay,
  endDay,
  onChange,
  onPick,
}) => {
  const t = useTranslator();
  const [pendingStart, setPendingStart] = useState<Date | null>(null);

  useEffect(() => {
    if (preset !== 'custom') {
      setPendingStart(null);
    }
  }, [preset]);

  const customLabel = useMemo(
    () => `${formatDay(startDay)} — ${formatDay(endDay)}`,
    [startDay, endDay],
  );

  const handlePresetPick = useCallback(
    (next: DateRangePreset) => {
      onChange(next, startDay, endDay);
      onPick?.(next);
    },
    [onChange, onPick, startDay, endDay],
  );

  const handleRangeChange = useCallback(
    (range: [Date | null, Date | null]) => {
      const [a, b] = range;
      if (pendingStart) {
        const isStart = (d: Date) => toUnixDay(d) === toUnixDay(pendingStart);
        const second = b && !isStart(b) ? b : a;
        if (!second) return;
        setPendingStart(null);
        const lo = Math.min(toUnixDay(pendingStart), toUnixDay(second));
        const hi = Math.max(toUnixDay(pendingStart), toUnixDay(second));
        onChange('custom', lo, hi);
        return;
      }
      if (a) setPendingStart(a);
    },
    [onChange, pendingStart],
  );

  return (
    <div className="DateRangeSection">
      <List>
        {PRESET_VALUES.map((value) => {
          const isActive = preset === value;
          const isCustom = value === 'custom';
          const indicator = isActive ? isCustom ? customLabel : <IcCheck16 /> : null;
          return (
            <ListItem
              key={value}
              onClick={() => handlePresetPick(value)}
              indicator={indicator}
              footer={
                isCustom && isActive ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <DatePicker
                      selectsRange
                      inline
                      startDate={pendingStart ?? fromUnixTime(startDay)}
                      endDate={pendingStart ? null : fromUnixTime(endDay)}
                      onChange={handleRangeChange as any}
                      minDate={MIN_DATE}
                      excludeDateIntervals={EXCLUDE_FUTURE}
                    />
                  </div>
                ) : null
              }
            >
              {t(PRESET_KEYS[value], { defaultValue: PRESET_DEFAULTS[value] })}
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};
